import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useFixedPopoverPosition } from './useFixedPopover';

/**
 * Small inline dropdown for swapping a named icon/logo (no freeform SVG or
 * upload widget anywhere in this codebase — matches that convention).
 * Portaled to <body> for the same reason as EditableImage/EditableLinkPath:
 * a plain absolutely-positioned popover here would sit inside its own
 * `.fsd-section`'s stacking context (scroll-reveal animates transform +
 * opacity, which creates one), and a later sibling section's own stacking
 * context can then visually paint over it, silently blocking clicks.
 */
export default function EditablePicker({
  editable = false,
  value,
  options,
  onChange,
  renderOption,
  children,
  color,
  onColorChange,
}) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const popoverRef = useRef(null);
  const pos = useFixedPopoverPosition(wrapRef, open, 'below', popoverRef);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (wrapRef.current?.contains(e.target)) return;
      if (popoverRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown, true);
    return () => document.removeEventListener('mousedown', onPointerDown, true);
  }, [open]);

  if (!editable) return children;

  const popover = open && pos ? (
    <div
      ref={popoverRef}
      className="svcpg-popover svcpg-popover--picker"
      style={{
        position: 'fixed',
        left: pos.left,
        top: pos.top,
        transform: pos.flipUp ? 'translateY(-100%)' : undefined,
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      {typeof onColorChange === 'function' ? (
        <label className="svcpg-popover__field svcpg-popover__field--row">
          <span>Icon color</span>
          <input type="color" value={color || '#1f4fe0'} onChange={(e) => onColorChange(e.target.value)} />
          {color ? (
            <button
              type="button"
              className="svcpg-toolbar__clear svcpg-toolbar__clear--on-light"
              onClick={() => onColorChange(null)}
              title="Reset to default color"
            >
              ×
            </button>
          ) : null}
        </label>
      ) : null}
      <div className="svcpg-picker-grid">
        {options.map((opt) => (
          <button
            key={opt.value}
            type="button"
            className={`svcpg-picker-opt${opt.value === value ? ' svcpg-picker-opt--active' : ''}`}
            onClick={() => {
              onChange(opt.value);
              setOpen(false);
            }}
            title={opt.label}
          >
            {renderOption ? renderOption(opt) : opt.label}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  return (
    <div className="svcpg-editable svcpg-editable-picker" ref={wrapRef}>
      <button
        type="button"
        className="svcpg-image-hit svcpg-image-hit--picker"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        aria-label="Change icon"
      >
        {children}
        <span className="svcpg-pencil svcpg-pencil--picker">✎</span>
      </button>
      {popover ? createPortal(popover, document.body) : null}
    </div>
  );
}
